/**
 * Tipos e chamada do painel.
 *
 * O campo que importa aqui é `fonte`. Ele diz se o bloco tem número de
 * verdade ou se está esperando um módulo que ainda não existe — é o que
 * permite à tela mostrar um estado vazio honesto em vez de um zero que
 * parece medição. Ver `api/internal/modulos/painel/dominio.go`.
 */
import { api } from './api'
import type { Centavos } from './dinheiro'

export type Fonte = 'real' | 'aguarda_venda' | 'aguarda_estoque'
export type Periodo = '7d' | '30d' | '12m'
export type Formato = 'dinheiro' | 'inteiro' | 'percentual'

export type Cabecalho = {
  saudacao: string
  data: string
  agora: string
}

export type Aviso = {
  tipo: string
  tom: 'atencao' | 'informacao'
  titulo: string
  detalhe: string
  acao?: string
  destino?: string
}

export type Variacao = {
  texto: string
  sentido: 'alta' | 'baixa' | 'estavel'
}

export type KPI = {
  chave: string
  rotulo: string
  formato: Formato
  valor: number
  fonte: Fonte
  variacao?: Variacao
  espera?: string
}

export type Barra = {
  rotulo: string
  valor_centavos: Centavos
}

export type Faturamento = {
  periodo: Periodo
  rotulo: string
  subtitulo: string
  fonte: Fonte
  total_centavos: Centavos
  barras: Barra[]
  espera?: string
}

export type LinhaGrade = {
  produto: string
  cor: string
  /** Segue a ordem de `tamanhos`. -1 é "esta cor não tem este tamanho". */
  saldos: number[]
}

export type GradeRisco = {
  fonte: Fonte
  tamanhos: string[]
  linhas: LinhaGrade[]
  espera?: string
}

export type LinhaTop = {
  produto: string
  categoria: string
  pecas: number
  receita_centavos: Centavos
}

export type Top = {
  fonte: Fonte
  linhas: LinhaTop[]
  espera?: string
}

export type FatiaMix = {
  categoria: string
  rotulo: string
  /** Pontos-base: 10000 = 100 %. */
  pontos_base: number
}

export type Mix = {
  fonte: Fonte
  base: 'catalogo' | 'receita'
  fatias: FatiaMix[]
}

export type Catalogo = {
  fonte: Fonte
  produtos: number
  skus: number
  unidades: number
}

export type Painel = {
  cabecalho: Cabecalho
  avisos: Aviso[]
  kpis: KPI[]
  faturamento: Faturamento
  grade_risco: GradeRisco
  top_produtos: Top
  mix: Mix
  catalogo: Catalogo
}

/** Busca o resumo do painel para o período pedido. */
export function carregarPainel(periodo: Periodo, sinal?: AbortSignal): Promise<Painel> {
  return api.get<Painel>(`/painel?periodo=${periodo}`, sinal)
}

/** Converte pontos-base em texto de porcentagem: 7500 → "75%". */
export function formatarPontos(pontos: number): string {
  const inteiro = Math.round(pontos / 100)
  return `${inteiro}%`
}
